import type { PieValueType } from '@mui/x-charts/models';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { formatFacetFilters } from '@/common/utils/formatFacetFilters';
import { theme } from '@/theme';
import type {
  DATE_RANGE_OPTIONS,
  DateInput,
} from '@/views/Home/components/FactsAndFigures/types';
import { useCore } from '$api-clients/hooks';
import {
  getEndDate,
  getStartDate,
  isValidDate,
} from '../../components/FactsAndFigures/components/DatesFilter.helpers';

const DATE_FORMAT = 'yyyy-MM-dd';
const OFFERS_STATUSES =
  'status:active,status:activeCont,status:backUp,status:pending,status:ratified,isArchived:false';
const LEASES_STATUSES =
  'status:active,status:activeCont,status:backUp,status:comingSoon,status:onMarket,status:pending,status:preListing,status:ratified,isArchived:false';
const LISTINGS_STATUSES =
  'status:active,status:activeCont,status:backUp,status:comingSoon,status:offMarket,status:onMarket,status:pending,status:preListing,isArchived:false';

export const statusConfigs: Record<string, { color: string; label: string }> = {
  active: { color: theme.palette.violet.main, label: 'Active' },
  activeCont: {
    color: theme.palette.secondary.dark,
    label: 'Active Contingent',
  },
  backUp: { color: theme.palette.periwinkle.main, label: 'Back Up' },
  comingSoon: {
    color: theme.palette.moonstone[400],
    label: 'Coming Soon',
  },
  pending: { color: theme.palette.secondary.main, label: 'Pending' },
  preListing: {
    color: '#F95D6A',
    label: 'Pre-Listing',
  },
  offMarket: {
    color: theme.palette.secondary[400] ?? '',
    label: 'Off Market',
  },
  onMarket: {
    color: '#D45087',
    label: 'On Market',
  },
  ratified: { color: theme.palette.moonstone.main, label: 'Ratified' },
};

type InteractionTypeFacetCounts = Record<string, string | null>;

const LEASES_STATUS_ORDER = [
  'pending',
  'onMarket',
  'active',
  'activeCont',
  'comingSoon',
  'preListing',
  'backUp',
  'ratified',
];

const DEFAULT_STATUS_ORDER = [
  'pending',
  'comingSoon',
  'onMarket',
  'active',
  'preListing',
  'activeCont',
  'backUp',
  'offMarket',
  'ratified',
];

/**
 * Returns the properties needed to render the facts and figures pie charts
 * using the facet counts received
 */
export function buildAugmentedData(
  interactionTypeFacetCounts: InteractionTypeFacetCounts,
  order = DEFAULT_STATUS_ORDER,
): PieValueType[] {
  const data = Object.keys(interactionTypeFacetCounts).reduce<
    (PieValueType & { status: string })[]
  >((acc, status) => {
    const value = parseInt(interactionTypeFacetCounts[status] ?? '0', 10);
    const { color = '', label = '' } = statusConfigs[status] ?? {};

    if (!color || !label || !value) return acc;

    acc.push({
      status,
      color,
      id: status,
      label,
      value,
    });

    return acc;
  }, []);

  return data.sort((a, b) => {
    const indexA = order.indexOf(a.status);
    const indexB = order.indexOf(b.status);

    // If status not found in order, put it at the end
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}

type UseFactsAndFiguresParams = {
  agents?: string[];
  endDate: DateInput;
  startDate: DateInput;
  dateRange: DATE_RANGE_OPTIONS;
  includeLeases?: boolean;
};

export function useFactsAndFigures(params: UseFactsAndFiguresParams) {
  const {
    agents = [],
    endDate: givenEndDate,
    startDate: givenStartDate,
    dateRange,
    includeLeases,
  } = params;

  const filters = [];

  if (agents.length) {
    agents.forEach((teamMemberId) => {
      filters.push({
        facet: 'teamMemberId',
        value: teamMemberId,
      });
    });
  }

  const startDate = getStartDate({
    dateRange,
    customStartDateValue: givenStartDate,
  });

  const endDate = getEndDate({
    dateRange,
    customEndDateValue: givenEndDate,
  });

  if (isValidDate(startDate) && isValidDate(endDate)) {
    filters.push({
      facet: 'createdAt',
      value: `[${format(startDate, DATE_FORMAT)} TO ${format(endDate, DATE_FORMAT)}]`,
    });
  }

  const formattedFilters = formatFacetFilters(filters);

  const { data: leasesData, isLoading: leasesLoading } = useCore(
    '/interactions',
    includeLeases
      ? {
          params: {
            query: {
              type: 'lease',
              facets: [formattedFilters, LEASES_STATUSES].join(','),
              includeFacetCountResults: true,
              limit: 1,
            },
          },
        }
      : null,
  );

  const { data: listingsData, isLoading: listingsLoading } = useCore(
    '/interactions',
    {
      params: {
        query: {
          type: 'listing',
          facets: [formattedFilters, LISTINGS_STATUSES].join(','),
          includeFacetCountResults: true,
          limit: 1,
        },
      },
    },
  );

  const { data: offersData, isLoading: offersLoading } = useCore(
    '/interactions',
    {
      params: {
        query: {
          type: 'offer',
          facets: [formattedFilters, OFFERS_STATUSES].join(','),
          includeFacetCountResults: true,
          limit: 1,
        },
      },
    },
  );

  const augmentedLeasesData = useMemo(
    () =>
      buildAugmentedData(leasesData?.facets?.status ?? {}, LEASES_STATUS_ORDER),
    [leasesData],
  );
  const augmentedListingsData = useMemo(
    () => buildAugmentedData(listingsData?.facets?.status ?? {}),
    [listingsData],
  );
  const augmentedOffersData = useMemo(
    () => buildAugmentedData(offersData?.facets?.status ?? {}),
    [offersData],
  );

  const augmentedData = useMemo(
    () => ({
      leases: augmentedLeasesData,
      listings: augmentedListingsData,
      offers: augmentedOffersData,
    }),
    [augmentedLeasesData, augmentedListingsData, augmentedOffersData],
  );

  return {
    data: augmentedData,
    loading: offersLoading || listingsLoading || leasesLoading,
  };
}
